"use client";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Header,
  Button,
  Table,
  Icon,
  Menu,
  Modal,
  Segment,
  Dropdown,
  Pagination,
  Loader,
  Message,
} from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";

import { slashAppBroadcasts } from "../../../utils/slashAppBroadcasts";
import { slashAppAnnouncements } from "../../../utils/slashAppAnnouncements";
import "../../../styles/scss/broadcastDetail.scss";

const STATUS_OPTIONS = [
  { key: "all", text: "すべて", value: "" },
  { key: "sent", text: "送信済み", value: "sent" },
  { key: "draft", text: "下書き", value: "draft" },
];

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function bubblePreview(bubble) {
  if (!bubble) return "(なし)";
  if (bubble.message_type === "text") {
    const t = (bubble.text || "").trim();
    return t.length > 40 ? `${t.slice(0, 40)}…` : t || "(空)";
  }
  if (bubble.message_type === "image") return "[画像]";
  if (bubble.message_type === "video") return "[動画]";
  return "(不明)";
}

function targetLabel(broadcast) {
  switch (broadcast.target_type) {
    case "all_customers":
      return "全顧客";
    case "studio":
      return `店舗 (${(broadcast.target_studios || []).length}件)`;
    case "custom":
      return `顧客指定 (${(broadcast.target_customers || []).length}名)`;
    case "recital2027":
      return "発表会2027";
    // インストラクターお知らせ (audience='instructor')。
    case "all_instructors":
      return "全インストラクター";
    default:
      return "-";
  }
}

// インストラクターお知らせの対象ラベル (custom は target_instructors を数える)。
function instructorTargetLabel(item) {
  if (item.target_type === "custom") {
    return `インストラクター指定 (${(item.target_instructors || []).length}名)`;
  }
  return "全インストラクター";
}

// 一覧の「メッセージ」列プレビュー。会員=最初のbubble / インストラクター=タイトル。
function messagePreview(item, audience) {
  if (audience === "instructor") {
    const t = (item.title || "").trim();
    return t.length > 40 ? `${t.slice(0, 40)}…` : t || "(無題)";
  }
  return bubblePreview((item.bubbles || [])[0]);
}

// send_status は会員一斉送信 (broadcasts) のみが持つ。裏側の配信処理の進捗で
// 「送信中 / 送信失敗」を出し分ける。インストラクターお知らせ (announcements)
// は send_status を持たないので、従来どおり is_sent / is_draft で判定する。
function statusLabel(broadcast) {
  if (broadcast.send_status === "sending") {
    return <span className="status-badge sending">送信中</span>;
  }
  if (broadcast.send_status === "failed") {
    return <span className="status-badge failed">送信失敗</span>;
  }
  if (broadcast.is_sent) {
    return <span className="status-badge sent">送信済み</span>;
  }
  if (broadcast.is_draft) {
    return <span className="status-badge draft">下書き</span>;
  }
  return <span className="status-badge pending">未配信</span>;
}

// 配信先（名前＋メール＋既読/未読タグ）の行レンダリング。
function peopleRows(list) {
  if (!list || list.length === 0) {
    return <div className="empty">配信先がありません</div>;
  }
  return list.map((r) => (
    <div key={r.user_id} className="person">
      <div className="p-info">
        <div className="p-name">{r.name || "(名前未登録)"}</div>
        <div className="p-mail">{r.email || `ID:${r.user_id}`}</div>
      </div>
      <span className={`p-read ${r.read ? "is-read" : ""}`}>
        {r.read ? "既読" : "未読"}
      </span>
    </div>
  ));
}

function BroadcastMessageListPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  // 配信チャネル: 会員一斉送信 (customer) / インストラクターお知らせ (instructor)。
  const [audience, setAudience] = useState("customer");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // 詳細表示モーダル。bubbles/stats は一覧APIに含まれるが、既読/未読の会員
  // 一覧は slash_admin の get_broadcast_recipients で別途取得する。
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [recipients, setRecipients] = useState({
    loading: false,
    read: [],
    unread: [],
    error: null,
  });

  const openDetailModal = async (broadcast) => {
    setDetailTarget(broadcast);
    setDetailOpen(true);
    // インストラクターお知らせは会員向けの配信先一覧API (get_broadcast_recipients)
    // を持たない。配信数/既読数は一覧APIの stats で表示するので取得しない。
    if (audience === "instructor") {
      setRecipients({ loading: false, read: [], unread: [], error: null });
      return;
    }
    setRecipients({ loading: true, read: [], unread: [], error: null });
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(
        `${apiUrl}/api/get_broadcast_recipients/?broadcast_id=${broadcast.id}`
      );
      if (!res.ok) throw new Error("配信先の取得に失敗しました");
      const data = await res.json();
      const all = data.recipients || [];
      setRecipients({
        loading: false,
        read: all.filter((r) => r.read),
        unread: all.filter((r) => !r.read),
        error: null,
      });
    } catch (e) {
      setRecipients({
        loading: false,
        read: [],
        unread: [],
        error: e.message || "配信先の取得に失敗しました",
      });
    }
  };

  // silent=true のときはローディング表示を出さずに裏で一覧を差し替える
  // (「送信中」→「送信済み/送信失敗」へのポーリング更新でチラつかせないため)。
  const fetchList = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const api =
        audience === "instructor" ? slashAppAnnouncements : slashAppBroadcasts;
      const data = await api.list({ page, status: statusFilter });
      setBroadcasts(data.results || []);
      const total = data.total || 0;
      const perPage = data.per_page || 1;
      setTotalPages(Math.max(1, Math.ceil(total / perPage)));
    } catch (e) {
      // ポーリング中のエラーは画面を壊さない (次回の再取得で回復)。
      if (!silent) setError(e.message || "データの取得に失敗しました");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, audience]);

  // 「送信中」の行がある間だけ数秒ごとに一覧を再取得し、裏側の配信が
  // 完了したら自動で「送信済み / 送信失敗」に切り替わるようにする。
  const hasSending = broadcasts.some((b) => b.send_status === "sending");
  useEffect(() => {
    if (!hasSending) return undefined;
    const timer = setInterval(() => fetchList({ silent: true }), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSending, page, statusFilter, audience]);

  const openDeleteModal = (broadcast) => {
    setDeleteTarget(broadcast);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const api =
        audience === "instructor" ? slashAppAnnouncements : slashAppBroadcasts;
      await api.remove(deleteTarget.id);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      // 削除後はリストを再取得 (ページネーション維持)
      fetchList();
    } catch (e) {
      setDeleteError(e.message || "削除に失敗しました");
    }
  };

  return (
    <div className="broadcast-message-list">
      <div className="page-header">
        <Header as="h1" className="page-title">
          {audience === "instructor" ? "インストラクターお知らせ一覧" : "一斉送信一覧"}
        </Header>
        <Link
          to={
            audience === "instructor"
              ? "/mypage/broadcast-message-edit?audience=instructor"
              : "/mypage/broadcast-message-edit"
          }
        >
          <Button primary icon labelPosition="left" className="create-button">
            <Icon name="plus" />
            {audience === "instructor" ? "新規お知らせ作成" : "新規一斉送信作成"}
          </Button>
        </Link>
      </div>

      {/* 会員一斉送信 / インストラクターお知らせ の切替 */}
      <Menu pointing secondary>
        <Menu.Item
          name="会員へ一斉送信"
          active={audience === "customer"}
          onClick={() => {
            setPage(1);
            setAudience("customer");
          }}
        />
        <Menu.Item
          name="インストラクターへお知らせ"
          active={audience === "instructor"}
          onClick={() => {
            setPage(1);
            setAudience("instructor");
          }}
        />
      </Menu>

      <Segment className="filter-section">
        <div className="filter-controls">
          <Dropdown
            selection
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(_, data) => {
              setPage(1);
              setStatusFilter(data.value);
            }}
            placeholder="ステータスで絞り込み"
            className="status-filter"
          />
        </div>
      </Segment>

      {loading ? (
        <Loader active inline="centered" content="データを読み込み中..." />
      ) : error ? (
        <Message negative>
          <Message.Header>エラー</Message.Header>
          <p>{error}</p>
          <Button primary onClick={fetchList}>
            再読み込み
          </Button>
        </Message>
      ) : broadcasts.length === 0 ? (
        <Message info>
          <Message.Header>データがありません</Message.Header>
          <p>
            {audience === "instructor"
              ? "条件に一致するお知らせがありません。"
              : "条件に一致する一斉送信がありません。"}
          </p>
        </Message>
      ) : (
        <>
          <Table celled inverted selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>メッセージ</Table.HeaderCell>
                <Table.HeaderCell>配信対象</Table.HeaderCell>
                <Table.HeaderCell>ステータス</Table.HeaderCell>
                <Table.HeaderCell>送信日時</Table.HeaderCell>
                <Table.HeaderCell>配信 / 既読</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {broadcasts.map((b) => {
                const isInstructor = audience === "instructor";
                const isSent = b.is_sent;
                const stats = b.stats || { delivered: 0, read: 0 };
                const editTo = isInstructor
                  ? `/mypage/broadcast-message-edit/${b.id}?audience=instructor`
                  : `/mypage/broadcast-message-edit/${b.id}`;
                return (
                  <Table.Row key={b.id}>
                    <Table.Cell>{messagePreview(b, audience)}</Table.Cell>
                    <Table.Cell>
                      {isInstructor ? instructorTargetLabel(b) : targetLabel(b)}
                    </Table.Cell>
                    <Table.Cell>{statusLabel(b)}</Table.Cell>
                    <Table.Cell>{formatDateTime(b.sent_at)}</Table.Cell>
                    <Table.Cell>
                      {stats.delivered} / {stats.read}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="action-buttons">
                        <button
                          className="bd-detail-btn"
                          onClick={() => openDetailModal(b)}
                        >
                          詳細
                        </button>
                        {/* 送信済みは編集も削除もできない */}
                        {!isSent && (
                          <>
                            <Button
                              as={Link}
                              to={editTo}
                              icon="edit"
                              size="tiny"
                            />
                            <Button
                              icon="trash"
                              size="tiny"
                              negative
                              onClick={() => openDeleteModal(b)}
                            />
                          </>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <div className="pagination-container">
              <Pagination
                activePage={page}
                onPageChange={(_, { activePage }) => setPage(activePage)}
                totalPages={totalPages}
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                nextItem={{ content: <Icon name="angle right" />, icon: true }}
              />
            </div>
          )}
        </>
      )}

      {/* 詳細表示ポップアップ（customer-list と同じダークテイスト） */}
      {detailOpen && detailTarget && (
        <div
          className="broadcast-detail-overlay"
          onClick={() => setDetailOpen(false)}
        >
          <div
            className="broadcast-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bd-header">
              <div className="bd-title-row">
                <span className="bd-title">メッセージ詳細</span>
                {statusLabel(detailTarget)}
              </div>
              <button
                className="bd-close"
                onClick={() => setDetailOpen(false)}
                aria-label="閉じる"
              >
                <Icon name="close" fitted />
              </button>
            </div>

            <div className="bd-body">
            {audience === "instructor" ? (
              <>
                <div className="bd-section">
                  <dl className="bd-info-grid">
                    <dt>配信対象</dt>
                    <dd>{instructorTargetLabel(detailTarget)}</dd>
                    <dt>送信日時</dt>
                    <dd>{formatDateTime(detailTarget.sent_at)}</dd>
                    <dt>配信 / 既読</dt>
                    <dd>
                      {(detailTarget.stats || {}).delivered || 0} /{" "}
                      {(detailTarget.stats || {}).read || 0}
                    </dd>
                  </dl>
                </div>
                <div className="bd-section">
                  <div className="bd-section-title">タイトル</div>
                  <div className="bd-bubble">{detailTarget.title}</div>
                </div>
                <div className="bd-section">
                  <div className="bd-section-title">本文</div>
                  <div className="bd-bubble" style={{ whiteSpace: "pre-wrap" }}>
                    {detailTarget.body}
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className="bd-section">
              <dl className="bd-info-grid">
                <dt>配信対象</dt>
                <dd>{targetLabel(detailTarget)}</dd>
                <dt>送信日時</dt>
                <dd>{formatDateTime(detailTarget.sent_at)}</dd>
              </dl>
            </div>

            <div className="bd-section">
              <div className="bd-section-title">メッセージ内容</div>
              <div className="bd-chat">
                {(detailTarget.bubbles || []).map((bubble) => (
                  <div key={bubble.id} className="bd-bubble-row">
                    {bubble.message_type === "text" && (
                      <div className="bd-bubble">{bubble.text}</div>
                    )}
                    {bubble.message_type === "image" && bubble.s3_url && (
                      <img
                        className="bd-bubble-media"
                        src={bubble.s3_url}
                        alt=""
                      />
                    )}
                    {bubble.message_type === "video" && bubble.s3_url && (
                      <video
                        className="bd-bubble-media"
                        src={bubble.s3_url}
                        controls
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bd-section">
              <div className="bd-section-title">
                配信先 ({recipients.unread.length + recipients.read.length})
              </div>
              {recipients.loading ? (
                <Loader
                  active
                  inline="centered"
                  size="small"
                  content="配信先を読み込み中..."
                />
              ) : recipients.error ? (
                <Message negative size="small">
                  {recipients.error}
                </Message>
              ) : (
                <div className="bd-people-list">
                  {peopleRows([...recipients.unread, ...recipients.read])}
                </div>
              )}
            </div>
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        size="tiny"
      >
        <Modal.Header>
          {audience === "instructor" ? "お知らせの削除" : "一斉送信の削除"}
        </Modal.Header>
        <Modal.Content>
          <p>
            {audience === "instructor"
              ? "このお知らせを削除してもよろしいですか？"
              : "この一斉送信を削除してもよろしいですか？"}
          </p>
          <p className="delete-message-text">
            {deleteTarget ? messagePreview(deleteTarget, audience) : ""}
          </p>
          {deleteError && (
            <Message negative>
              <p>{deleteError}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeleteModalOpen(false)}>キャンセル</Button>
          <Button negative onClick={handleDelete}>
            削除
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default BroadcastMessageListPage;
